import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Download, Search, TrendingDown } from "lucide-react";
import { useState, useMemo, JSX } from "react";
import { resources } from "@/data";
import { cn } from "@/lib/utils";
import { addNotification } from "@/store";

export const Route = createFileRoute("/_public/resources")({
  component: ResourcesPage,
});

const categories = [
  "All",
  "Compliance",
  "eTIMS",
  "Corporate Tax",
  "PAYE & Payroll",
  "International Tax",
  "Updates",
];

export function ResourcesPage(): JSX.Element {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    return resources.filter((r) => {
      const matchesCat =
        activeCategory === "All" || r.category === activeCategory;
      const matchesSearch =
        !searchQuery ||
        r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const handleDownload = (title: string) => {
    addNotification("success", `Downloading "${title}"...`);
  };

  return (
    <>
      <Helmet>
        <title>Tax Resources | Rosewood Tax Consultancy Kenya</title>
        <meta
          name="description"
          content="Free Kenyan tax resources: filing calendars, eTIMS guides, PAYE templates, and more. Download expert resources from Rosewood Tax Consultancy."
        />
      </Helmet>

      <section className="bg-cream pt-12 sm:pt-16 pb-10 sm:pb-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className=" text-4xl sm:text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-4">
              Free Tax Resources
            </h1>
            <p className="text-base md:text-lg text-neutral-800">
              Download guides, templates, checklists and calendars to keep your
              business tax‑compliant in Kenya.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-10 sm:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className=" text-xl sm:text-2xl font-semibold text-[#1A1A1A] mb-5 sm:mb-6">
            Most Downloaded
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {resources
              .filter((r) => r.featured)
              .map((resource, i) => (
                <motion.div
                  key={resource.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex flex-col sm:flex-row gap-3 p-4 sm:p-5 border rounded-xl hover:shadow-xs transition-all"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-lg mb-1 line-clamp-1">
                      {resource.title}
                    </p>
                    <p className="text-neutral-800 mb-2 line-clamp-2 sm:line-clamp-2">
                      {resource.description}
                    </p>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-1 text-sm text-neutral-800">
                        <TrendingDown size={10} />
                        {resource.downloads.toLocaleString()} downloads
                      </div>
                      <button
                        onClick={() => handleDownload(resource.title)}
                        className="flex items-center gap-1 text-sm font-semibold text-burgundy hover:text-[#4A1219] transition-colors"
                      >
                        <Download size={12} />
                        {resource.fileSize}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row gap-4 mb-8 sm:mb-10">
            <div className="relative w-full md:max-w-md">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-800"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources..."
                className="w-full pl-11 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#6B1E2A]/40 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={cn(
                    "px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm font-medium transition-all",
                    activeCategory === cat
                      ? "bg-burgundy text-white"
                      : "bg-cream text-neutral-800",
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {filtered.map((resource, i) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 p-4 sm:p-5 border rounded-xl hover:shadow-xs transition-all group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg md:text-base">
                      {resource.title}
                    </h3>
                    <span className="px-2 py-0.5 text-sm bg-burgundy/8 text-burgundy font-semibold rounded uppercase">
                      {resource.type}
                    </span>
                    <span className="px-2 py-0.5 text-sm bg-[#FAF0E8] text-[#A07832] font-medium rounded">
                      {resource.category}
                    </span>
                  </div>
                  <p className="text-neutral-800 line-clamp-2 md:line-clamp-1">
                    {resource.description}
                  </p>
                </div>
                <div className="flex items-center justify-between md:justify-end gap-6 mt-1 md:mt-0 flex-shrink-0">
                  <div className="hidden md:flex flex-col items-end">
                    <span className="text-sm font-medium text-[#1A1A1A]">
                      {resource.fileSize}
                    </span>
                    <span className="text-sm text-neutral-800">
                      {resource.downloads.toLocaleString()} downloads
                    </span>
                  </div>
                  <button
                    onClick={() => handleDownload(resource.title)}
                    className="flex items-center justify-center gap-2 px-4 py-2 w-full md:w-auto bg-burgundy text-white text-sm font-semibold rounded-lg hover:bg-[#4A1219] transition-colors"
                  >
                    <Download size={13} />
                    Download
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-neutral-800">
                No resources found matching your search.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
